import { useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { usePushSubscription } from '../hooks/usePushSubscription.js'
import { useCurrentUnit } from '../hooks/useCurrentUnit.js'

// NotificationsSection — operator-only push opt-in card, rendered inside
// the isOperator-gated area of SettingsScreen.
//
// v1 sends ONLY a manual test push (no recommendations, no business
// actions). The card lets the operator enable/disable web push on this
// device and fire a single test notification through send-test-push.
//
// Sentence case, no emoji, no AI-cost / model language. All hooks are
// declared before any conditional render below.
export default function NotificationsSection() {
  // All hooks declared before any conditional render (React hook-ordering rule).
  const { state, busy, message, enable, disable } = usePushSubscription()
  const { currentRestaurantId } = useCurrentUnit()

  const [testing, setTesting] = useState(false)
  const [testInfo, setTestInfo] = useState('')
  const [testError, setTestError] = useState('')

  // Morning-brief on-demand send state. Kept separate from the test-push
  // state so the two buttons report independently.
  const [briefSending, setBriefSending] = useState(false)
  const [briefInfo, setBriefInfo] = useState('')
  const [briefError, setBriefError] = useState('')

  const subscribed = state === 'subscribed'

  async function handleSendBrief() {
    if (briefSending) return
    setBriefSending(true)
    setBriefInfo('')
    setBriefError('')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/send-morning-brief`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
          apikey: anonKey,
        },
        // on_demand path: include the current unit id so a grouped operator
        // gets the SELECTED unit's brief (membership-verified server-side).
        // Single-unit (null) → the fn defaults to the caller's first unit.
        body: JSON.stringify({
          on_demand: true,
          ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
        }),
      })

      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || data?.error) {
        setBriefError("We couldn't send your morning brief. Try again.")
        return
      }
      // The fn returns { mode: 'on_demand', outcome }. 'quiet' means nothing
      // material to flag today; everything else is a successful send attempt.
      if (data?.outcome === 'quiet') {
        setBriefInfo('Quiet day — nothing major to flag.')
      } else if (data?.outcome === 'no_subscription') {
        setBriefError('No push subscription found. Enable push first.')
      } else {
        setBriefInfo('Your morning brief is on its way.')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[NotificationsSection] send brief failed', e)
      setBriefError("We couldn't send your morning brief. Try again.")
    } finally {
      setBriefSending(false)
    }
  }

  async function handleSendTest() {
    if (testing) return
    setTesting(true)
    setTestInfo('')
    setTestError('')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/send-test-push`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
          apikey: anonKey,
        },
        body: JSON.stringify({}),
      })

      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || data?.error) {
        if (resp.status === 404 || data?.error === 'no_subscription') {
          setTestError('No push subscription found. Enable push first.')
        } else {
          setTestError("We couldn't send a test notification. Try again.")
        }
        return
      }
      // The fn returns 200 with { sent, removed } even when every per-device
      // send failed (sent:0). Only claim success when at least one device was
      // actually reached — otherwise the optimistic "should arrive" message
      // would lie. Tailor the nudge when an expired subscription was cleaned
      // up so the operator knows to re-enable.
      const sent = Number(data?.sent) || 0
      const removed = Number(data?.removed) || 0
      if (sent <= 0) {
        // Surface the push service's exact status/reason (non-secret) so a
        // failed send is diagnosable from the device — e.g. a 403 BadJwtToken
        // points at a VAPID key/subject mismatch.
        const first = Array.isArray(data?.results) ? data.results[0] : null
        const diag = first?.status
          ? ` (push service: ${first.status}${first.reason ? ` ${String(first.reason).slice(0, 80)}` : ''})`
          : ''
        if (removed > 0) {
          setTestError(
            "This device's subscription expired — turn push off and on to re-enable." + diag,
          )
        } else {
          setTestError(
            "We couldn't reach this device. Try turning push off and on again." + diag,
          )
        }
        return
      }
      setTestInfo('Test notification sent. It should arrive on this device shortly.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[NotificationsSection] send test failed', e)
      setTestError("We couldn't send a test notification. Try again.")
    } finally {
      setTesting(false)
    }
  }

  return (
    <section
      className="mt-6 rounded-2xl border border-black/10 bg-white p-4 shadow-sm"
      aria-label="Push notifications"
    >
      <div className="text-xs font-medium text-black/55">Push notifications</div>
      <p className="mt-1 text-xs text-black/60 leading-snug">
        Get push notifications on this device. Turn it on, then send yourself a test
        to confirm it works.
      </p>

      {state === 'unsupported' && (
        <div className="mt-3 rounded-md bg-black/5 px-3 py-2 text-sm text-black/70">
          This browser doesn't support push notifications.
        </div>
      )}

      {state === 'ios-needs-install' && (
        <div className="mt-3 rounded-md bg-black/5 px-3 py-2 text-sm text-black/70">
          On iPhone, add this app to your home screen first, then enable push from
          the installed app.
        </div>
      )}

      {state === 'denied' && (
        <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Notifications are blocked in your browser settings. Enable them for this
          site to receive push.
        </div>
      )}

      {state !== 'unsupported' &&
        state !== 'ios-needs-install' &&
        state !== 'denied' && (
          <>
            <div className="mt-3 flex gap-2">
              {!subscribed ? (
                <button
                  type="button"
                  onClick={enable}
                  disabled={busy}
                  className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? 'Turning on…' : 'Enable push'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={disable}
                  disabled={busy}
                  className="flex-1 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {busy ? 'Turning off…' : 'Disable push'}
                </button>
              )}
              <button
                type="button"
                onClick={handleSendTest}
                disabled={!subscribed || testing}
                className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {testing ? 'Sending…' : 'Send test notification'}
              </button>
            </div>

            <div className="mt-2">
              <button
                type="button"
                onClick={handleSendBrief}
                disabled={!subscribed || briefSending}
                className="w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {briefSending ? 'Sending…' : 'Send my morning brief now'}
              </button>
            </div>

            {message && (
              <div className="mt-2 text-[11px] text-black/55 leading-snug">{message}</div>
            )}
          </>
        )}

      {testInfo && (
        <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {testInfo}
        </div>
      )}
      {testError && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {testError}
        </div>
      )}

      {briefInfo && (
        <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {briefInfo}
        </div>
      )}
      {briefError && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {briefError}
        </div>
      )}
    </section>
  )
}
